import ApplicationController from './application_controller'

/**
 * CheckoutCtaController
 *
 * Manages the floating/sticky "Proceed to Checkout" button on the opportunity page.
 * Updates item count and total when cart changes.
 */
export default class extends ApplicationController {
  static targets = [
    'itemCount',  // Span showing item count
    'total',      // Span showing total price
    'button',     // The checkout button itself
    'container'   // Main container (for show/hide)
  ]

  static values = {
    checkoutUrl: String,
    itemCount: Number,
    total: Number
  }

  connect() {
    super.connect()
    // Set defaults for Stimulus v2
    if (!this.hasItemCountValue) this.itemCountValue = 0
    if (!this.hasTotalValue) this.totalValue = 0

    // Listen for cart update events
    this.handleCartUpdate = this.handleCartUpdate.bind(this)
    document.addEventListener('cartUpdated', this.handleCartUpdate)

    // Initial UI update
    this.updateUI()
  }

  disconnect() {
    document.removeEventListener('cartUpdated', this.handleCartUpdate)
  }

  /**
   * Handle cart updated events
   */
  handleCartUpdate(event) {
    const { cart } = event.detail

    if (cart) {
      this.itemCountValue = cart.item_count || 0
      this.totalValue = cart.total || 0
      this.updateUI()
    }
  }

  /**
   * Update the UI based on current values
   */
  updateUI() {
    const count = this.itemCountValue
    const total = this.totalValue

    // Update item count display
    if (this.hasItemCountTarget) {
      const itemText = count === 1 ? 'item' : 'items'
      this.itemCountTarget.textContent = `(${count} ${itemText})`
    }

    // Update total display
    if (this.hasTotalTarget) {
      this.totalTarget.textContent = this.formatCurrency(total)
    }

    // Show/hide container based on whether there are items
    if (this.hasContainerTarget) {
      if (count > 0) {
        this.containerTarget.classList.remove('d-none')
        this.animateIn()
      } else {
        this.containerTarget.classList.add('d-none')
      }
    }

    // Enable/disable button
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = count === 0
    }
  }

  /**
   * Animate the CTA appearing
   */
  animateIn() {
    if (this.hasContainerTarget) {
      this.containerTarget.classList.add('checkout-cta-animate')
      setTimeout(() => {
        this.containerTarget.classList.remove('checkout-cta-animate')
      }, 300)
    }
  }

  /**
   * Navigate to checkout
   */
  checkout(event) {
    if (this.itemCountValue === 0) {
      event.preventDefault()
      return
    }
    // Allow default link behavior to proceed to checkout
  }

  /**
   * Format number as currency
   */
  formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }
}
